<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div v-if="realSlides.length === 0" class="carousel__empty-state">Add slides in the editor</div>
  <div
    v-else
    class="carousel"
    :class="{
      'carousel--extends-outside': extendsOutside,
    }"
    :style="carouselStyle"
  >
    <div
      class="carousel__content"
      :class="{ 'carousel__content--positioned': arrowCenteringMode === 'content' }"
    >
      <div
        ref="viewportRef"
        class="carousel__viewport"
        :style="viewportStyle"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
      >
        <div
          ref="trackRef"
          class="carousel__track"
          :style="trackStyle"
          @transitionend="onTransitionEnd"
        >
          <div
            v-for="(item, domIndex) in renderSlides"
            :key="item.key"
            class="carousel__slide"
            :class="{ 'carousel__slide--center': domIndex === currentIndex }"
            :style="slideStyle"
            :tabindex="item.isGhost ? -1 : 0"
            :inert="item.isGhost ? true : undefined"
            :aria-hidden="item.isGhost ? 'true' : undefined"
            @focusin="onSlideFocusIn(item, domIndex)"
          >
            <RenderSlot
              :name="item.sourceId"
              :children="slotChildren?.[item.sourceId]"
              :editable="item.isGhost ? false : editable"
            >
              <div class="carousel__empty">
                <template v-if="item.isGhost">(Preview)</template>
                <template v-else>Drop content for slide {{ item.displayIndex + 1 }}</template>
              </div>
            </RenderSlot>
          </div>
        </div>
      </div>

      <template v-if="showArrows && !arrowsInline">
        <CarouselArrow
          direction="prev"
          :hidden="isPrevHidden"
          :sprite="fields?.arrowSprite"
          @click="goToPrev"
        />
        <CarouselArrow
          direction="next"
          :hidden="isNextHidden"
          :sprite="fields?.arrowSprite"
          @click="goToNext"
        />
      </template>
    </div>

    <!-- Dot indicators, and the arrows when they sit below the slides.
         `click-passthrough-region` on the row keeps clicks and hover from
         resolving to the Carousel when the cursor is on navigation UI — otherwise
         the Carousel hover outline flickers over content hover outlines. -->
    <div
      v-if="showDots || (showArrows && arrowsInline)"
      class="carousel__nav click-passthrough-region"
      :class="{ 'carousel__nav--below': arrowCenteringMode === 'below' }"
    >
      <CarouselArrow
        v-if="showArrows && arrowsInline"
        direction="prev"
        inline
        :hidden="isPrevHidden"
        :sprite="fields?.arrowSprite"
        @click="goToPrev"
      />
      <div v-if="showDots" class="carousel__dots" :style="dotsStyle">
        <button
          v-for="(dot, index) in dots"
          :key="dot.id"
          class="carousel__dot click-passthrough-region"
          :class="{ 'carousel__dot--active': dot.active, 'carousel__dot--sprite': !!dot.sprite }"
          tabindex="-1"
          :aria-label="`Go to slide ${index + 1}`"
          @click="goToSlide(index)"
        >
          <img v-if="dot.sprite" v-bind="dot.sprite.imgAttrs" alt="" class="carousel__dot-sprite" />
        </button>
      </div>
      <CarouselArrow
        v-if="showArrows && arrowsInline"
        direction="next"
        inline
        :hidden="isNextHidden"
        :sprite="fields?.arrowSprite"
        @click="goToNext"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick, onMounted, onUnmounted } from 'vue'
import type { ComponentNode } from '../../../../api/types.gen'
import type { CarouselSlideConfig } from './types'
import {
  getColorStyleData,
  getSizeStyleData,
  getSpacingStyleData,
} from '../../../composables/useStyleData'
import { useImagePreset } from '../../../composables/useImagePreset'
import { usePipe, PipeDisplayMode } from '../../../composables/usePipe'
import { useDirection } from '../../../composables/useDirection'
import RenderSlot from '../../../RenderSlot.vue'
import CarouselArrow from './CarouselArrow.vue'
import { useCarouselSwipe } from './useCarouselSwipe'
import { useCarouselAutoplay } from './useCarouselAutoplay'
import type { CarouselFields } from './definition'

interface RenderSlideItem {
  key: string
  sourceId: string
  displayIndex: number
  isGhost: boolean
}

const props = withDefaults(
  defineProps<{
    fields?: CarouselFields
    slotChildren?: Record<string, ComponentNode[]>
    editable?: boolean
  }>(),
  {
    fields: () => ({}),
  },
)

const viewportRef = ref<HTMLElement | null>(null)
const trackRef = ref<HTMLElement | null>(null)
const viewportWidth = ref(0)

// --- Slide data ---

const realSlides = computed<CarouselSlideConfig[]>(() => props.fields?.slides?.items ?? [])

const isLoop = computed(() => props.fields?.loop ?? true)
const transitionMs = computed(() => props.fields?.transitionDuration ?? 400)

const cloneCount = computed(() => {
  const count = realSlides.value.length
  if (!count || !isLoop.value) return 0
  return Math.max(2, Math.ceil(3 / count))
})

const ghostOffset = computed(() => cloneCount.value * realSlides.value.length)

const maxDomIndex = computed(() => renderSlides.value.length - 1)

const renderSlides = computed<RenderSlideItem[]>(() => {
  const real = realSlides.value
  if (!real.length) return []

  const main: RenderSlideItem[] = real.map((s, i) => ({
    key: s.id,
    sourceId: s.id,
    displayIndex: i,
    isGhost: false,
  }))

  if (!isLoop.value) return main

  const before: RenderSlideItem[] = []
  const after: RenderSlideItem[] = []
  const cc = cloneCount.value

  for (let c = 0; c < cc; c++) {
    for (let i = 0; i < real.length; i++) {
      before.push({
        key: `ghost-b-${c}-${real[i]!.id}`,
        sourceId: real[i]!.id,
        displayIndex: i,
        isGhost: true,
      })
      after.push({
        key: `ghost-a-${c}-${real[i]!.id}`,
        sourceId: real[i]!.id,
        displayIndex: i,
        isGhost: true,
      })
    }
  }

  return [...before, ...main, ...after]
})

// --- Index state ---

const currentIndex = ref(0)
const isTransitioning = ref(false)

// Initialize currentIndex to first real slide
watch(
  [realSlides, isLoop],
  () => {
    if (!realSlides.value.length) {
      currentIndex.value = 0
      return
    }
    currentIndex.value = ghostOffset.value
  },
  { immediate: true },
)

// Keep index valid when slides change
watch(realSlides, (items) => {
  if (!items.length) return
  // If the current real index is out of bounds, clamp
  const realIdx = currentRealIndex.value
  if (realIdx >= items.length) {
    currentIndex.value = ghostOffset.value + items.length - 1
  }
})

const currentRealIndex = computed(() => {
  const realCount = realSlides.value.length
  if (!realCount) return 0
  if (!isLoop.value) return currentIndex.value
  const offset = ghostOffset.value
  return (((currentIndex.value - offset) % realCount) + realCount) % realCount
})

// --- Arrow options ---

const arrowCenteringMode = computed(() => props.fields?.arrowCentering ?? 'content')
const arrowsInline = computed(
  () => arrowCenteringMode.value === 'below' || arrowCenteringMode.value === 'dots',
)
const showArrows = computed(
  () => props.fields?.showArrows !== false && (!!props.fields?.loop || realSlides.value.length > 1),
)
const isPrevHidden = computed(() => !props.fields?.loop && currentRealIndex.value === 0)
const isNextHidden = computed(
  () => !props.fields?.loop && currentRealIndex.value === realSlides.value.length - 1,
)

// --- Dot options ---

const showDots = computed(() => props.fields?.showDots !== false && realSlides.value.length > 1)
const dotSpriteProps = useImagePreset(() => props.fields?.dotSprite)
const activeDotSpriteProps = useImagePreset(() => props.fields?.activeDotSprite)

// Each state falls back to the drawn dot on its own, so a sprite set for only
// one of them still leaves the active dot distinguishable.
const dots = computed(() =>
  realSlides.value.map((slide, index) => {
    const active = index === currentRealIndex.value
    return {
      id: slide.id,
      active,
      sprite: active ? activeDotSpriteProps.value : dotSpriteProps.value,
    }
  }),
)

// Set on `.carousel__dots` rather than the root so a carousel nested in a
// slide does not inherit them.
const dotsStyle = computed(() => {
  const s: Record<string, string> = {}
  const color = getColorStyleData(props.fields?.dotColor)
  if (color) s['--carousel-dot-color'] = color
  const activeColor = getColorStyleData(props.fields?.activeDotColor)
  if (activeColor) s['--carousel-dot-active-color'] = activeColor
  const size = getSizeStyleData(props.fields?.dotSize)
  if (size) s['--carousel-dot-size'] = size
  const activeSize = getSizeStyleData(props.fields?.activeDotSize)
  if (activeSize) s['--carousel-dot-active-size'] = activeSize
  return s
})

// --- Sizing ---

const enablePreviews = computed(() => props.fields?.enablePreviews ?? true)
const extendsOutside = computed(
  () => enablePreviews.value && (props.fields?.overflowVisible ?? false),
)

const gapCss = computed(() => getSizeStyleData(props.fields?.gap, '0px'))
const previewWidthCss = computed(() =>
  enablePreviews.value ? getSizeStyleData(props.fields?.previewWidth, '40px') || '40px' : '0px',
)
const previewFadeCss = computed(() => getSizeStyleData(props.fields?.previewFadeDistance, '40px'))
const minHeightCss = computed(() => getSizeStyleData(props.fields?.minSlideHeight))

// Measure viewport width with ResizeObserver
let resizeObserver: ResizeObserver | null = null
let viewportRaf = 0
let latestViewportWidth = 0

onMounted(() => {
  if (viewportRef.value) {
    viewportWidth.value = viewportRef.value.offsetWidth
    // Defer the reactive write to avoid
    // "ResizeObserver loop completed with undelivered notifications".
    resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1]
      if (!entry) return
      latestViewportWidth = entry.contentRect.width
      if (viewportRaf) return
      viewportRaf = requestAnimationFrame(() => {
        viewportRaf = 0
        viewportWidth.value = latestViewportWidth
      })
    })
    resizeObserver.observe(viewportRef.value)
  }
})

onUnmounted(() => {
  resizeObserver?.disconnect()
  if (viewportRaf) cancelAnimationFrame(viewportRaf)
})

// --- Computed styles ---

const carouselStyle = computed(() => {
  const s: Record<string, string> = {}
  const padding = getSpacingStyleData(props.fields?.padding)
  if (padding) s.padding = padding
  return s
})

const viewportStyle = computed(() => {
  const s: Record<string, string> = {}
  const pw = previewWidthCss.value || '0px'
  const g = gapCss.value || '0px'

  if (extendsOutside.value) {
    // Make viewport wider than carousel by (previewWidth + gap) on each side.
    // Slides remain calculated from viewport's 100%, which now resolves to
    // (carouselWidth + 2*previewWidth + 2*gap), making slide width = carouselWidth.
    // The viewport's negative margins push it visually outside the carousel bounds.
    s.width = `calc(100% + 2 * (${pw} + ${g}))`
    s.marginLeft = `calc(-1 * (${pw} + ${g}))`
    s.marginRight = `calc(-1 * (${pw} + ${g}))`
  }

  if (enablePreviews.value && pw !== '0px') {
    const fd = previewFadeCss.value || '40px'
    // Vertical padding + negative margin extends the mask area so box-shadows
    // on child components are not clipped at the viewport's top/bottom edge.
    const shadowRoom = '40px'
    s.paddingTop = shadowRoom
    s.paddingBottom = shadowRoom
    s.marginTop = `-${shadowRoom}`
    s.marginBottom = `-${shadowRoom}`
    const mask = `linear-gradient(to right, transparent 0%, black ${fd}, black calc(100% - ${fd}), transparent 100%)`
    s.maskImage = mask
    s.WebkitMaskImage = mask
  }
  return s
})

const slideStyle = computed(() => {
  const s: Record<string, string> = {}
  const pw = previewWidthCss.value || '0px'
  const g = gapCss.value || '0px'
  s.width = `calc(100% - 2 * ${pw} - 2 * ${g})`
  s.flexShrink = '0'
  const mh = minHeightCss.value
  if (mh) s.minHeight = mh
  return s
})

const trackStyle = computed(() => {
  const total = renderSlides.value.length
  if (!total) return {}

  const pw = previewWidthCss.value || '0px'
  const g = gapCss.value || '0px'

  // Each slide occupies: calc((100% - 2*pw - 2*g) + g) in the track
  // translateX positions the active slide so that its left edge starts after the preview area
  // We use calc() to let the browser handle mixed units (px, rem, %, etc.)
  const idx = currentIndex.value
  const slideAndGap = `(100% - 2 * ${pw} - 2 * ${g} + ${g})`
  const offset = `calc(-1 * ${idx} * ${slideAndGap} + ${pw} + ${g})`

  // Under RTL the flex track lays slides out right-to-left, so the physical
  // translateX (which `dir` does NOT mirror) must move the opposite way for the
  // index-driven offset.
  const sign = isRtl.value ? -1 : 1

  const s: Record<string, string> = {
    display: 'flex',
    gap: g,
    // Mirror only the index-driven `offset` (logical layout math). `dragOffset`
    // is a raw physical screen-pixel delta from the pointer — the track must
    // follow the finger in screen space, so it is NOT sign-flipped.
    transform: `translateX(calc(${sign} * (${offset}) + ${dragOffset.value}px))`,
    willChange: 'transform',
  }

  if (isTransitioning.value && dragOffset.value === 0) {
    s.transition = `transform ${transitionMs.value}ms ease`
  } else {
    s.transition = 'none'
  }

  return s
})

// --- Navigation ---

let isNormalizing = false

async function normalizeIndex(delta: number) {
  const track = trackRef.value
  if (!track) return

  // Silent teleport: ghost clones at (idx) and (idx + delta) render identical
  // content, so when the track is at rest this shift is visually seamless.
  isTransitioning.value = false
  currentIndex.value += delta

  // Wait for Vue to flush the DOM update (transition:none + new transform).
  await nextTick()

  // Force the browser to commit the intermediate style as the starting point
  // for the next transition — otherwise the following transform change may
  // animate from the pre-teleport position instead of the teleported one.
  void track.offsetHeight
}

async function goToNext() {
  const realCount = realSlides.value.length
  if (!realCount) return
  if (isNormalizing) return

  if (isLoop.value) {
    if (currentIndex.value + 1 > maxDomIndex.value) {
      isNormalizing = true
      await normalizeIndex(-realCount)
      isNormalizing = false
    }
    isTransitioning.value = true
    currentIndex.value += 1
  } else if (currentRealIndex.value < realCount - 1) {
    isTransitioning.value = true
    currentIndex.value += 1
  }
  autoplay.notifyInteraction()
}

async function goToPrev() {
  const realCount = realSlides.value.length
  if (!realCount) return
  if (isNormalizing) return

  if (isLoop.value) {
    if (currentIndex.value - 1 < 0) {
      isNormalizing = true
      await normalizeIndex(realCount)
      isNormalizing = false
    }
    isTransitioning.value = true
    currentIndex.value -= 1
  } else if (currentRealIndex.value > 0) {
    isTransitioning.value = true
    currentIndex.value -= 1
  }
  autoplay.notifyInteraction()
}

function goToSlide(realIdx: number) {
  isTransitioning.value = true
  currentIndex.value = ghostOffset.value + realIdx
  autoplay.notifyInteraction()
}

// When focus lands on a non-center slide (by Tab, click on a descendant, or
// programmatic), centre the carousel on that slide so the physical focus
// position matches the active slide. Ghost clones are `inert` so they never
// receive focus here.
function onSlideFocusIn(item: RenderSlideItem, domIndex: number) {
  if (item.isGhost) return
  if (domIndex === currentIndex.value) return
  goToSlide(item.displayIndex)
}

// --- Silent reposition on loop boundary ---

function onTransitionEnd(e: TransitionEvent) {
  if (e.target !== trackRef.value) return
  isTransitioning.value = false

  if (!isLoop.value) return
  const realCount = realSlides.value.length
  if (!realCount) return

  const offset = ghostOffset.value
  const idx = currentIndex.value

  if (idx < offset || idx >= offset + realCount) {
    const realIdx = (((idx - offset) % realCount) + realCount) % realCount
    currentIndex.value = offset + realIdx
  }
}

// --- Swipe ---

// Under RTL the slides advance right-to-left, so the swipe gesture meaning
// inverts: a left-swipe goes to the previous slide, a right-swipe to the next.
const { dragOffset, onPointerDown, onPointerMove, onPointerUp } = useCarouselSwipe(viewportRef, {
  onSwipeLeft: () => (isRtl.value ? goToPrev() : goToNext()),
  onSwipeRight: () => (isRtl.value ? goToNext() : goToPrev()),
  onInteraction: () => autoplay.notifyInteraction(),
})

// When drag ends and commits to a slide change, we need the transition
watch(dragOffset, (val, oldVal) => {
  if (oldVal !== 0 && val === 0) {
    // Drag just ended — the goToNext/goToPrev already set isTransitioning
    // But if the swipe was below threshold, we need a snap-back transition
    isTransitioning.value = true
    // Clear transition after it completes in case transitionend doesn't fire (no actual movement)
    setTimeout(() => {
      isTransitioning.value = false
    }, transitionMs.value + 50)
  }
})

// --- Editor selection: pause autoplay and centre on selected child ---
//
// In EDITOR mode, when the user selects a descendant component the carousel
// shouldn't shuffle out from under them — pause autoplay while a descendant
// is active, and slide to that descendant's slide so it's centred.
//
// Detection is DOM-based: walk up from the selected element to the first
// `.carousel__slide` that is a direct child of OUR `trackRef` (avoids
// matching the slides of an outer/nested carousel). Ghost clones and the
// real slide share the same `displayIndex`, so any of them is fine.

const pipe = usePipe()
const { isRtl } = useDirection()
const selectedDescendantDisplayIndex = ref<number | null>(null)

function findSelectedSlideDisplayIndex(nodeId: string): number | null {
  const track = trackRef.value
  if (!track) return null
  const matches = document.querySelectorAll<HTMLElement>(`[data-id="${CSS.escape(nodeId)}"]`)
  for (const match of matches) {
    let cur: HTMLElement | null = match
    while (cur) {
      if (cur.classList.contains('carousel__slide') && cur.parentElement === track) {
        const domIndex = Array.from(track.children).indexOf(cur)
        const item = renderSlides.value[domIndex]
        if (item) return item.displayIndex
        return null
      }
      cur = cur.parentElement
    }
  }
  return null
}

watch(
  [() => pipe.activeNodeId.value, () => pipe.currentMode.value, realSlides],
  async ([activeId, mode]) => {
    if (mode !== PipeDisplayMode.EDITOR || !activeId) {
      selectedDescendantDisplayIndex.value = null
      return
    }
    // Wait a tick so a freshly-added/moved component is in the DOM before we
    // probe for it (covers tree-add, drag-drop, and SharedNodeRef re-renders).
    await nextTick()
    const idx = findSelectedSlideDisplayIndex(activeId)
    selectedDescendantDisplayIndex.value = idx
    if (idx !== null && idx !== currentRealIndex.value) {
      goToSlide(idx)
    }
  },
  { immediate: true },
)

// --- Autoplay ---

const autoplay = useCarouselAutoplay({
  enabled: computed(
    () => (props.fields?.autoPlay ?? false) && selectedDescendantDisplayIndex.value === null,
  ),
  interval: computed(() => props.fields?.autoPlayInterval ?? 4000),
  cooldown: computed(() => props.fields?.autoPlayCooldown ?? 8000),
  onTick: () => goToNext(),
})
</script>

<style scoped>
.carousel {
  position: relative;
  width: 100%;
  /* Within-bounds (default): the only clip on the slide track — neither the
     viewport nor the track clips itself. */
  overflow-x: clip;
}

/* Chrome < 90 and Safari < 16 (iOS 15 and older) drop `clip`, and the unclipped
   track then widens the whole page, which a phone zooms out to fit. `hidden`
   also clips vertically and makes a scroll container, so only they get it. */
@supports not (overflow-x: clip) {
  .carousel:not(.carousel--extends-outside) {
    overflow: hidden;
  }
}

.carousel--extends-outside {
  /* Allow the viewport to extend visually beyond the carousel boundary.
     NOTE: This relies on a parent container (or the page body) clipping its
     own horizontal overflow to prevent a horizontal scrollbar appearing. */
  overflow-x: visible;
}

.carousel__content--positioned {
  position: relative;
}

.carousel__viewport {
  position: relative;
  width: 100%;
  touch-action: pan-y;
}

.carousel__track {
  display: flex;
}

.carousel__slide {
  flex: 0 0 auto;
}

.carousel__slide--center:focus-visible {
  outline: none;
  box-shadow: var(--shadow-selectors-default);
  border-radius: var(--radius-md);
}

.carousel__empty-state,
.carousel__empty {
  min-height: 48px;
  border: var(--border-width) dashed var(--color-border-default);
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-tertiary);
  font-family: var(--text-body-sm-family);
  font-size: var(--text-body-sm-size);
  line-height: var(--text-body-sm-line-height);
  padding: var(--space-8);
  opacity: 0.7;
  user-select: none;
}

/* Navigation row — the dots, and the arrows when they sit below the slides */

.carousel__nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-8);
  padding: var(--space-8) 0 var(--space-4);
}

/* Arrows at the row's edges, the dots centred between them. */
.carousel__nav--below {
  justify-content: space-between;
}

.carousel__nav--below .carousel__dots {
  flex: 1;
}

/* Dots — the --carousel-dot-* properties are set only when the author picked
   a value, so an unset one falls back to the theme default here. */

.carousel__dots {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-4);
}

.carousel__dot {
  width: var(--carousel-dot-size, 8px);
  height: var(--carousel-dot-size, 8px);
  border-radius: 50%;
  border: none;
  background: var(--carousel-dot-color, var(--color-border-subtle));
  cursor: pointer;
  padding: 0;
  transition:
    background-color 0.2s,
    width 0.2s,
    height 0.2s;
}

.carousel__dot:hover {
  background: var(--carousel-dot-active-color, var(--color-border-accent));
}

.carousel__dot--active {
  width: var(--carousel-dot-active-size, var(--carousel-dot-size, 8px));
  height: var(--carousel-dot-active-size, var(--carousel-dot-size, 8px));
  background: var(--carousel-dot-active-color, var(--color-action-primary-background));
}

/* Dots — sprite mode: the size is the image's height, its width follows the
   image's aspect ratio. */

.carousel__dot--sprite,
.carousel__dot--sprite:hover {
  width: auto;
  height: auto;
  border-radius: 0;
  background: transparent;
  line-height: 0;
}

.carousel__dot-sprite {
  display: block;
  height: var(--carousel-dot-size, 8px);
  width: auto;
}

.carousel__dot--active .carousel__dot-sprite {
  height: var(--carousel-dot-active-size, var(--carousel-dot-size, 8px));
}
</style>
